import { useMutation, useQueryClient } from 'react-query'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { BrandKitAvatar } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { brandKitAvatarsCacheKey } from './useBrandKitAvatars'

interface Data {
  avatar: BrandKitAvatar
}

export interface UpdateAvatarVariables {
  id: number
  name?: string
  media_file_id?: number
  persona_note?: string
  age?: string
  ethnicity?: string
  enabled?: boolean
  voice_preset?: string | null
  voice_media_file_id?: number | null
}

type Options = Omit<UseMutationOptions<Data, Error, UpdateAvatarVariables>, 'mutationFn'>

export const useUpdateBrandKitAvatar = ({ brandKitId }: { brandKitId?: number }, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, UpdateAvatarVariables>(
    async ({ id, ...variables }) => {
      if (!brandKitId) throw new Error('brandKitId is required')
      const res = await sendToApi<Data>(`/w/${workspaceId}/brand_kits/${brandKitId}/avatars/${id}`, {
        method: 'PUT',
        body: variables,
      })
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Failed to update avatar')
      }
      return res.data
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(brandKitAvatarsCacheKey(workspaceId, brandKitId))
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
